import { Box, Checkbox, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { DARK_TABLE_BORDER, DARK_TABLE_HEADER_BG, DARK_TEXT_MUTED, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import DarkPagination from './dark-pagination'
import type { DarkColumn, DarkRowKey, DarkTableProps } from './types'

// Visible border + control colour for the dark row-select checkboxes.
const checkboxSx = {
  '.chakra-checkbox__control': { borderColor: 'rgba(255, 255, 255, 0.35)', bg: 'transparent' }
}

function defaultCell<T>(row: T, col: DarkColumn<T>) {
  const value = (row as Record<string, unknown>)[col.key]
  return value == null ? '' : String(value)
}

// Generic, presentational dark-mode table. The parent supplies column defs and
// row data; no feature logic lives here so it can be reused across pages.
function DarkTable<T>({
  columns,
  data,
  rowKey,
  isLoading,
  emptyMessage = 'No data',
  onRowClick,
  selectable,
  selectedKeys = [],
  onSelectionChange,
  showPagination,
  pagination
}: DarkTableProps<T>) {
  const colSpan = columns.length + (selectable ? 1 : 0)
  const selected = new Set<DarkRowKey>(selectedKeys)
  const pageKeys = data.map((row, i) => rowKey(row, i))
  const allSelected = pageKeys.length > 0 && pageKeys.every(k => selected.has(k))
  const someSelected = pageKeys.some(k => selected.has(k)) && !allSelected

  const toggleAll = () => {
    if (!onSelectionChange) return
    onSelectionChange(allSelected ? [] : pageKeys)
  }

  const toggleOne = (key: DarkRowKey) => {
    if (!onSelectionChange) return
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onSelectionChange([...next])
  }

  const headCellSx = {
    bg: 'transparent',
    border: 'none',
    color: DARK_TEXT_MUTED,
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none' as const,
    letterSpacing: 'normal',
    whiteSpace: 'nowrap' as const,
    // Phones give a table ~390px total: a fixed 16px gutter on every cell eats
    // ~30% of it before any content, which is what squeezed the Stacks title
    // into a 3-line wrap. Same 8/10px rhythm the Plans mobile rows use.
    px: { base: '12px', md: '16px' },
    py: 0,
    h: '48px'
  }

  return (
    <Box borderRadius="0" overflow="hidden" w="full">
      <Table w="full" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <Thead>
          <Tr background={DARK_TABLE_HEADER_BG}>
            {selectable && (
              <Th
                sx={{
                  ...headCellSx,
                  w: { base: '36px', md: '48px' },
                  px: { base: '8px', md: '16px' },
                  textAlign: 'center'
                }}
              >
                <Checkbox isChecked={allSelected} isIndeterminate={someSelected} onChange={toggleAll} sx={checkboxSx} />
              </Th>
            )}
            {columns.map(col => (
              <Th
                key={col.key}
                display={col.hideOnMobile ? { base: 'none', md: 'table-cell' } : undefined}
                sx={{ ...headCellSx, textAlign: col.align ?? 'left', w: col.width, minW: col.minWidth }}
              >
                {col.header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={colSpan} border="none" py="40px" textAlign="center">
                <Spinner color={DARK_TEXT_MUTED} />
              </Td>
            </Tr>
          ) : data.length === 0 ? (
            // Falsy emptyMessage => render nothing (caller wants a blank surface).
            emptyMessage ? (
              <Tr>
                <Td colSpan={colSpan} border="none" py="40px" textAlign="center">
                  <Text color={DARK_TEXT_MUTED} m={0}>
                    {emptyMessage}
                  </Text>
                </Td>
              </Tr>
            ) : null
          ) : (
            data.map((row, i) => {
              const key = rowKey(row, i)
              return (
                <Tr
                  key={key}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  _hover={{ bg: 'rgba(255, 255, 255, 0.04)' }}
                >
                  {selectable && (
                    <Td
                      borderBottom={`1px solid ${DARK_TABLE_BORDER}`}
                      borderTop="none"
                      h="48px"
                      px={{ base: '8px', md: '16px' }}
                      width={{ base: '36px', md: '48px' }}
                      textAlign="center"
                      onClick={e => e.stopPropagation()}
                    >
                      <Checkbox
                        isChecked={selected.has(key)}
                        onChange={() => toggleOne(key)}
                        onClick={e => e.stopPropagation()}
                        sx={checkboxSx}
                      />
                    </Td>
                  )}
                  {columns.map(col => (
                    <Td
                      key={col.key}
                      display={col.hideOnMobile ? { base: 'none', md: 'table-cell' } : undefined}
                      borderBottom={`1px solid ${DARK_TABLE_BORDER}`}
                      borderTop="none"
                      h="48px"
                      px={{ base: '12px', md: '16px' }}
                      color={DARK_TEXT_PRIMARY}
                      fontSize="14px"
                      textAlign={col.align ?? 'left'}
                    >
                      {col.hideWhen?.(row) ? null : col.render ? col.render(row, i) : defaultCell(row, col)}
                    </Td>
                  ))}
                </Tr>
              )
            })
          )}
        </Tbody>
      </Table>
      {showPagination && pagination ? <DarkPagination {...pagination} /> : null}
    </Box>
  )
}

export default DarkTable
